import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/slices/store";
import { buildCacheKey } from "src/slices/TabData/reducer";
import { usePaginatedTabData } from "src/hooks/usePaginatedTabData";
import { TabDefinition, TabRequestContext } from "./tabRegistry";

interface ConnectedTabProps {
  tabDefinition:  TabDefinition;
  exchangeId:     string | number;
  requestContext: TabRequestContext;
}

const ConnectedTab = ({ tabDefinition, exchangeId, requestContext }: ConnectedTabProps) => {
  const TableComponent = tabDefinition.tableComponent!;
  const tabData = usePaginatedTabData({
    tabDefinition,
    exchangeId,
    requestContext,
    isActive: true, // mounted only while active
  });

  const cacheKey = buildCacheKey(exchangeId, tabDefinition.tabId);
  const selectedMetrics = useSelector(
    (state: RootState) => state.tabData.entries[cacheKey]?.selectedMetrics
  );

  const requiredColumnIds = useMemo(
    () => tabDefinition.requiredColumns?.map((c) => c.id) ?? [],
    [tabDefinition.requiredColumns]
  );

  // required columns (pinned first) + selected metrics (user-ordered)
  // undefined = show all columns (before permitted_metrics is loaded)
  const visibleColumns = useMemo(() => {
    if (selectedMetrics === undefined) return undefined;     // not loaded → show all
    if (selectedMetrics.length === 0) return [];             // user cleared all → no data
    return [...requiredColumnIds, ...selectedMetrics];
  }, [requiredColumnIds, selectedMetrics]);

  return (
    <TableComponent
      rows={tabData.rows}
      isLoading={tabData.isLoading}
      currentPage={tabData.currentPage}
      totalPages={tabData.totalPages}
      totalRecords={tabData.totalRecords}
      perPage={tabData.perPage}
      sortColumn={tabData.sortColumn}
      sortDirection={tabData.sortDirection}
      exchangeId={exchangeId}
      onSortChange={tabData.onSortChange}
      onPageChange={tabData.onPageChange}
      onPerPageChange={tabData.onPerPageChange}
      visibleColumns={visibleColumns}
    />
  );
};

export default ConnectedTab;
